import classnames from "classnames";
import moment from "moment";
import React from "react";
import { textFilter } from "react-bootstrap-table2-filter";
import { UncontrolledTooltip } from "reactstrap";
import Popover from "../../components/Popover/Popover";
import i18n from "../../config/locale/i18n";
import {
  BOT_STATUS_FILTER_KEYS,
  BOT_TYPE_COMPLIANCE,
  BOT_TYPE_SECURITY,
  COMPLIANCE_BOT_ID,
  COMPLIANT,
  CRITICAL,
  FAILED,
  HIGH,
  INFORMATIONAL,
  LOW,
  MEDIUM,
  NONCOMPLIANT,
  PASSED,
  SECURITY_BOT_ID,
  SUPPRESSED,
} from "../../utils/app-constants.json";
import {
  getArrayValues,
  isArrayWithLength,
  isSetWithSize,
} from "../../utils/array-methods";
import complianceBotResources from "../../utils/compliance-bot-resources.json";
import { capitalizeFirstLetter } from "../../utils/format-string";
import { isEmptyObject } from "../../utils/object-methods";
import {
  getSelectedOrgs,
  getSelectedOrgsAndDepts,
} from "../../utils/org-params";
import securityBotResources from "../../utils/security-bot-resources.json";
import { isAdminUserFn } from "../../utils/users";

export const getBotId = (type) => {
  let botId = "";
  switch (type) {
    case BOT_TYPE_SECURITY:
      botId = SECURITY_BOT_ID;
      break;
    case BOT_TYPE_COMPLIANCE:
      botId = COMPLIANCE_BOT_ID;
      break;
    default:
      break;
  }
  return botId;
};

export const getBotName = (botId) => {
  let name = "";
  switch (botId) {
    case SECURITY_BOT_ID:
      name = i18n.t("common:security");
      break;
    case COMPLIANCE_BOT_ID:
      name = i18n.t("common:compliance");
      break;
    default:
      break;
  }
  return name;
};

export const getBotType = (botId) => {
  let type = "";
  switch (botId) {
    case SECURITY_BOT_ID:
      type = BOT_TYPE_SECURITY;
      break;
    case COMPLIANCE_BOT_ID:
      type = BOT_TYPE_COMPLIANCE;
      break;
    default:
      break;
  }
  return type;
};

export const formatter = (cell, className, row) => {
  return (
    cell && (
      <>
        <span className={classnames("circle status", className)} />
        &nbsp; <span>{capitalizeFirstLetter(cell).replace("_", " ")}</span>
      </>
    )
  );
};

export const severityFormatter = (cell) => {
  const className = cell && cell.toLowerCase();
  return formatter(cell, className);
};

export const statusFormatter = (cell, row) => {
  const className = [PASSED, COMPLIANT].includes(cell) ? "low" : "critical";
  return formatter(cell, className, row);
};

export const updatedAtFormatter = (cell) => {
  let dateTime = moment(cell).format("YYYY-MM-DD HH:mm A");
  let statusTime = moment(cell).fromNow();
  let targetId = `insight-date-time-${moment(cell).valueOf()}`;
  return (
    <>
      <span id={targetId}>{statusTime}</span>
      <UncontrolledTooltip placement="bottom" target={targetId}>
        {dateTime}
      </UncontrolledTooltip>
    </>
  );
};

const getFormatedPolicy = (cell, id, showContent) => {
  const content = cell[0].PolicyGroup;
  if (showContent) {
    return content;
  }
  return (
    <Popover
      text={
        <span className="ml-1">
          {cell.length} {i18n.t("common:policies")}
        </span>
      }
      id={`insights-policies-${id}`}
      header={i18n.t("common:policies")}
      content={content}
      placement="auto-start"
    />
  );
};

export const policyFormatter = (policies, row) => {
  if (isArrayWithLength(policies) && policies.length > 1) {
    return getFormatedPolicy(policies, row.id);
  } else if (isArrayWithLength(policies)) {
    const showContent = true;
    return getFormatedPolicy(policies, row.id, showContent);
  }
  return null;
};

export const getReportFilters = (getState, predefinedFilters) => {
  let filters = {};
  const { commons, globalConfig } = getState();
  const { multiScopeFilters, multiTreeViewFilters } = commons;
  filters = getSelectedOrgs(globalConfig.filteredUserData);
  const scopeFilters = multiScopeFilters && multiScopeFilters.filters;
  if (scopeFilters) {
    if (isSetWithSize(scopeFilters.accounts)) {
      filters.Accounts = Array.from(scopeFilters.accounts);
    }
    if (isSetWithSize(scopeFilters.applications)) {
      filters.Apps = Array.from(scopeFilters.applications);
    }
    if (isSetWithSize(scopeFilters.departments)) {
      filters.Depts = Array.from(scopeFilters.departments);
    }
  }
  if (multiTreeViewFilters) {
    if (isArrayWithLength(multiTreeViewFilters.Statuses)) {
      filters.Statuses = getArrayValues(multiTreeViewFilters.Statuses);
    }
    if (isArrayWithLength(multiTreeViewFilters.PolicyGroups)) {
      filters.PolicyGroups = getArrayValues(multiTreeViewFilters.PolicyGroups);
    }
    if (isArrayWithLength(multiTreeViewFilters.ResourceTypes)) {
      filters.ResourceTypes = getArrayValues(
        multiTreeViewFilters.ResourceTypes
      );
    }
  }
  if (predefinedFilters) {
    !isEmptyObject(predefinedFilters) &&
      Object.keys(predefinedFilters).forEach((filter) => {
        filters[filter] = predefinedFilters[filter];
      });
  }

  return filters;
};

const getDashboardFilters = (getState, insightFilters, parentId) => {
  const { globalConfig } = getState();
  let filters = getSelectedOrgsAndDepts(globalConfig.filteredUserData);
  const isAdminUser = isAdminUserFn();

  if (isAdminUser) {
    delete filters.Depts;
  }

  if (insightFilters) {
    if (isArrayWithLength(insightFilters.accountFilters)) {
      filters.Accounts = getArrayValues(insightFilters.accountFilters);
    }
    if (isArrayWithLength(insightFilters.regionFilters)) {
      filters.Regions = getArrayValues(insightFilters.regionFilters);
    }
    if (isArrayWithLength(insightFilters.resourceFilters)) {
      filters.ResourceTypes = getArrayValues(insightFilters.resourceFilters);
    }
    if (isArrayWithLength(insightFilters.applicationFilters)) {
      filters.Apps = getArrayValues(insightFilters.applicationFilters);
    }
    if (isArrayWithLength(insightFilters.statusFilters)) {
      filters.Statuses = getArrayValues(insightFilters.statusFilters);
    }
    if (isArrayWithLength(insightFilters.policyGroupsFilters)) {
      filters.PolicyGroups = getArrayValues(insightFilters.policyGroupsFilters);
    }
    if (isArrayWithLength(insightFilters.projectFilters)) {
      filters.Depts = getArrayValues(insightFilters.projectFilters);
    }
  }
  if (parentId) {
    filters.Apps = [parentId];
  }
  return filters;
};

export const getBotFilters = ({
  getState,
  insightFilters,
  parentId,
  isReportGeneration,
  getQueryParams,
  appliedFilters,
  predefinedFilters,
}) => {
  let filters = {};
  if (isReportGeneration && !appliedFilters) {
    filters = getReportFilters(getState, predefinedFilters);
  } else {
    filters = getDashboardFilters(
      getState,
      appliedFilters || insightFilters,
      parentId
    );
  }

  if (!getQueryParams) {
    return filters;
  }
  const queryParams = "Filters=" + encodeURI(JSON.stringify(filters));
  return queryParams;
};

export const getBotInsightFilters = (type) => {
  return type === BOT_TYPE_SECURITY
    ? [
        {
          label: i18n.t("common:passedLabel"),
          value: BOT_STATUS_FILTER_KEYS.PASSED,
        },
        {
          label: i18n.t("common:failedLabel"),
          value: BOT_STATUS_FILTER_KEYS.FAILED,
        },
        {
          label: i18n.t("common:suppressedLabel"),
          value: BOT_STATUS_FILTER_KEYS.SUPPRESSED,
        },
      ]
    : [
        {
          label: i18n.t("common:compliantLabel"),
          value: BOT_STATUS_FILTER_KEYS.COMPLIANT,
        },
        {
          label: i18n.t("common:nonCompliantLabel"),
          value: BOT_STATUS_FILTER_KEYS.NONCOMPLIANT,
        },
      ];
};

export const getBotResourceTypes = (type) =>
  type === BOT_TYPE_COMPLIANCE ? complianceBotResources : securityBotResources;

export const RESOURCE_OBJ_KEYS = {
  VPC: "VpcId",
  SUBNET: "SubnetIds",
  ROUTE_TABLE: "RouteTableId",
};

const commonColumns = [
  {
    dataField: "resourceId",
    text: i18n.t("common:id"),
    sort: true,
    filter: textFilter({ placeholder: i18n.t("common:search") }),
  },
  {
    dataField: "resourceName",
    text: i18n.t("common:nameLabel"),
    sort: true,
    filter: textFilter({ placeholder: i18n.t("common:search") }),
  },
];

const cidrColumn = {
  dataField: "cidrBlock",
  text: i18n.t("resources:cidrBlock"),
  sort: true,
  filter: textFilter({ placeholder: i18n.t("common:search") }),
};

export const resourceColumns = {
  [RESOURCE_OBJ_KEYS.VPC]: [...commonColumns, cidrColumn],
  [RESOURCE_OBJ_KEYS.SUBNET]: [...commonColumns, cidrColumn],
  [RESOURCE_OBJ_KEYS.ROUTE_TABLE]: [...commonColumns],
};

export const RESOURCE_SUMMARY = "ResourceSummary";
export const STATUS_SUMMARY = "StatusSummary";
export const SEVERITY_SUMMARY = "SeveritySummary";
export const OPEN_ISSUES = "OpenIssueSummary";
export const TOP_FIVE_CHECKS = "TopFiveCheckSummary";
export const TOP_FIVE_RESOURCES = "TopFiveResourceSummary";
export const INDUSTRY_STANDARDS = "IndustryStandards";

export const botSummaryTypeReducerMap = {
  ResourceSummary: "botResourceSummary",
  StatusSummary: "botStatusSummary",
  SeveritySummary: "botSeveritySummary",
  OpenIssueSummary: "botOpenIssues",
  TopFiveCheckSummary: "botTopFiveChecks",
  TopFiveResourceSummary: "botTopFiveResources",
  IndustryStandards: "botIndustryStandards",
};

export const getBotColumnField = (botType, field) => {
  let botColumnMap = {
    severity: "Severity",
    status:
      botType === BOT_TYPE_COMPLIANCE ? "ComplianceType" : "InsightStatus",
    description: "Description",
    policies: "Policies",
    title: "Title",
    updatedAt:
      botType === BOT_TYPE_COMPLIANCE ? "ResultRecordedTime" : "UpdatedAt",
    resourceId: "ResourceId",
    resourceLabel: "ResourceType",
    departments: "Depts",
    accountName: "AccountNumber",
    accountNumber: "AccountNumber",
    regionName: "RegionCode",
    regionCode: "RegionCode",
  };
  return botColumnMap[field];
};

export const getBotStatusOptions = (type) => {
  return type === BOT_TYPE_SECURITY
    ? [
        {
          label: i18n.t("common:passedLabel"),
          value: PASSED,
        },
        {
          label: i18n.t("common:failedLabel"),
          value: FAILED,
        },
        {
          label: i18n.t("common:suppressedLabel"),
          value: SUPPRESSED,
        },
      ]
    : [
        {
          label: i18n.t("common:compliantLabel"),
          value: COMPLIANT,
        },
        {
          label: i18n.t("common:nonCompliantLabel"),
          value: NONCOMPLIANT,
        },
      ];
};

export const getBotSeverityOptions = () => {
  return [
    {
      label: i18n.t("common:critical"),
      value: CRITICAL,
    },
    {
      label: i18n.t("common:high"),
      value: HIGH,
    },
    {
      label: i18n.t("common:informational"),
      value: INFORMATIONAL,
    },
    {
      label: i18n.t("common:medium"),
      value: MEDIUM,
    },
    {
      label: i18n.t("common:low"),
      value: LOW,
    },
  ];
};

const buildColumn = ({
  field,
  headerName,
  sortable,
  filter,
  filterParams,
  cellRendererFramework,
}) => {
  const column = {
    field,
    headerName,
    sortable,
    filter,
    filterParams,
  };
  if (cellRendererFramework) {
    column.cellRendererFramework = cellRendererFramework;
  }
  return column;
};

export const getColumns = (items) => {
  return isArrayWithLength(items)
    ? items.map((item) => {
        return buildColumn(item);
      })
    : [];
};

export const insightsChartTooltip = (title, content, color) => {
  return `
  <div class="ag-chart-tooltip-title d-flex align-items-center justify-content-center" style="background-color: ${color}">
      ${title}
  </div>
  <div class="ag-chart-tooltip-content d-flex align-items-center justify-content-center mc-text-base-bold">
      ${content}
  </div>`;
};
